/**
 * Accuracy as a bar plus its number — and, when it is known, the denominator.
 *
 * Deliberately single-hue: the design system reserves green/amber/red for exam
 * semantics and the accent for the primary action, so a "red = bad" ramp here
 * would collide with the violation palette. The bar carries magnitude, the
 * percentage carries the value, and topics below the weak threshold get an
 * explicit "weak" label — meaning is never in the fill alone.
 *
 * The sample size matters as much as the percentage. A topic where the student
 * answered one question and got it wrong is 0%, and a bar pinned to zero next
 * to a topic missed nine times out of twelve reads as the same emergency. So an
 * accuracy drawn from fewer than `MIN_SAMPLE` answers is labelled "n too small"
 * instead of "weak": the number is still shown, but it is not presented as a
 * finding.
 */
import { cn } from "@/lib/cn";
import { formatPercent } from "../lib/format";

/** Below this, a topic is called out rather than left to be eyeballed. */
export const WEAK_THRESHOLD = 0.5;

/** Fewer answers than this and a percentage is noise, not a measurement. */
export const MIN_SAMPLE = 4;

export function AccuracyBar({
  accuracy,
  answered,
  showWeakLabel = true,
  className,
}: {
  /** 0..1 */
  accuracy: number;
  /**
   * How many answers the percentage is drawn from. Optional only because a
   * couple of call sites predate it; pass it wherever it is known.
   */
  answered?: number;
  showWeakLabel?: boolean;
  className?: string;
}) {
  const clamped = Math.max(0, Math.min(1, accuracy));
  const weak = clamped < WEAK_THRESHOLD;
  const thin = answered !== undefined && answered < MIN_SAMPLE;

  return (
    <div className={cn("flex items-center gap-2", className)}>
      <div
        className="h-1.5 min-w-16 flex-1 overflow-hidden rounded-xs bg-surface-sunken"
        role="img"
        aria-label={
          answered === undefined
            ? `${formatPercent(clamped)} correct`
            : `${formatPercent(clamped)} correct, from ${answered} answered ${
                answered === 1 ? "question" : "questions"
              }`
        }
      >
        <div
          className="h-full rounded-xs bg-ink-muted"
          style={{ width: `${clamped * 100}%` }}
        />
      </div>
      <span className="tabular w-9 shrink-0 text-right text-2xs text-ink-muted">
        {formatPercent(clamped)}
      </span>
      {answered !== undefined ? (
        <span className="tabular w-10 shrink-0 text-2xs text-ink-faint">
          n={answered}
        </span>
      ) : null}
      {showWeakLabel && weak ? (
        thin ? (
          <span className="shrink-0 rounded-sm border border-border px-1 text-2xs font-medium text-ink-faint">
            n too small
          </span>
        ) : (
          <span className="shrink-0 rounded-sm border border-violation/25 bg-violation-soft px-1 text-2xs font-medium text-violation">
            weak
          </span>
        )
      ) : null}
    </div>
  );
}
